import { v4 as uuidv4 } from 'uuid';

import {
  type BatchSelection,
  batchSelectionSchema
} from './batchHandoff.schema';

const STORAGE_PREFIX = 'stratumn.batchSelection.';

const storageKey = (batchKey: string): string => `${STORAGE_PREFIX}${batchKey}`;

export const newBatchKey = (): string => uuidv4();

export function stashBatchSelection(
  batchKey: string,
  selection: BatchSelection
): void {
  try {
    sessionStorage.setItem(storageKey(batchKey), JSON.stringify(selection));
  } catch {
    // sessionStorage unavailable (private mode / quota). The history state
    // remains the primary channel; only reload recovery is lost.
  }
}

export function readBatchSelection(batchKey: string): BatchSelection | null {
  try {
    const raw = sessionStorage.getItem(storageKey(batchKey));
    if (!raw) {
      return null;
    }
    const parsed = batchSelectionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function clearBatchSelection(batchKey: string): void {
  try {
    sessionStorage.removeItem(storageKey(batchKey));
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
}

type BatchFormSearchArgs = {
  groupKey: string;
  actionKey: string;
  batchKey: string;
  traceCount: number;
  traceId?: string;
};

/**
 * Query string for the newLink form in batch mode. Constant length whatever the
 * selection size: the trace ids travel in the history state + sessionStorage,
 * never here. A ~215-id list already overflows the 8 KB ingress URL limit.
 */
export function buildBatchFormSearch({
  groupKey,
  actionKey,
  batchKey,
  traceCount,
  traceId
}: BatchFormSearchArgs): string {
  const parts = [
    `groupKey=${encodeURIComponent(groupKey)}`,
    `actionKey=${encodeURIComponent(actionKey)}`
  ];
  if (traceId) {
    parts.push(`traceId=${encodeURIComponent(traceId)}`);
  }
  parts.push(
    `traceCount=${traceCount}`,
    `batchKey=${encodeURIComponent(batchKey)}`
  );
  return `?${parts.join('&')}`;
}

type BatchFormTargetArgs = {
  baseUrl: string;
  groupKey: string;
  actionKey: string;
  selection: BatchSelection;
  traceCount: number;
};

/**
 * Stash the selection and return the newLink path that references it. The first
 * id doubles as the representative trace the form hydrates.
 */
export function createBatchFormTarget({
  baseUrl,
  groupKey,
  actionKey,
  selection,
  traceCount
}: BatchFormTargetArgs): { path: string; batchKey: string } {
  const batchKey = newBatchKey();
  stashBatchSelection(batchKey, selection);
  const traceId = selection.mode === 'explicit' ? selection.ids[0] : undefined;
  return {
    path: `${baseUrl}${buildBatchFormSearch({
      groupKey,
      actionKey,
      batchKey,
      traceCount,
      traceId
    })}`,
    batchKey
  };
}
